import { useState } from 'react';
import { ArrowLeft, ThumbsDown, ThumbsUp } from 'lucide-react';
import type { HelpArticle } from '../types';
import { MediaPlayer } from './MediaPlayer';

interface ArticleDetailProps {
  article: HelpArticle;
  onBack: () => void;
  /** Reportar feedback a analytics desde el padre. */
  onFeedback?: (articleId: string, positive: boolean) => void;
}

export function ArticleDetail({ article, onBack, onFeedback }: ArticleDetailProps) {
  const [feedback, setFeedback] = useState<boolean | null>(null);

  function handleFeedback(positive: boolean) {
    setFeedback(positive);
    onFeedback?.(article.id, positive);
  }

  return (
    <div className="pb-5 pt-1">
      <button
        type="button"
        onClick={onBack}
        className="mb-3.5 flex items-center gap-1.5 pt-1.5 text-caption-medium text-muted hover:text-secondary"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Volver
      </button>

      <span className="mb-2 inline-block rounded-full bg-brand-light px-2.5 py-0.5 text-label font-semibold text-brand">
        {article.category}
      </span>
      <h3 className="mb-1.5 text-h4 leading-snug text-primary">{article.title}</h3>
      <p className="mb-3.5 text-caption leading-relaxed text-muted">{article.snippet}</p>

      {article.media && <MediaPlayer media={article.media} />}

      <ol className="flex flex-col gap-3">
        {article.steps.map((step, i) => (
          <li key={i} className="flex gap-2.5 text-ui-sm leading-relaxed text-secondary">
            <span className="mt-0.5 flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-full bg-brand-light text-label font-bold text-brand">
              {i + 1}
            </span>
            <span>{step}</span>
          </li>
        ))}
      </ol>

      <div className="mt-5 flex items-center justify-between border-t border-muted pt-4">
        <span className="text-caption text-muted">
          {feedback === null
            ? '¿Te ayudó esta guía?'
            : feedback
              ? '¡Gracias por tu feedback!'
              : 'Gracias, lo revisaremos.'}
        </span>
        <div className="flex gap-1.5">
          <button
            type="button"
            aria-label="Sí me ayudó"
            onClick={() => handleFeedback(true)}
            className={
              'flex h-8 w-8 items-center justify-center rounded-md border transition-colors ' +
              (feedback === true
                ? 'border-success-border bg-success-bg text-success'
                : 'text-tertiary hover:bg-surface-hover')
            }
          >
            <ThumbsUp className="h-[15px] w-[15px]" />
          </button>
          <button
            type="button"
            aria-label="No me ayudó"
            onClick={() => handleFeedback(false)}
            className={
              'flex h-8 w-8 items-center justify-center rounded-md border transition-colors ' +
              (feedback === false
                ? 'border-strong bg-surface-2 text-secondary'
                : 'text-tertiary hover:bg-surface-hover')
            }
          >
            <ThumbsDown className="h-[15px] w-[15px]" />
          </button>
        </div>
      </div>
    </div>
  );
}
